import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Crown, Zap } from "lucide-react";
import { toast } from "sonner";
import CompactPackageCard from "@/components/CompactPackageCard";
import PackageComparisonTable from "@/components/PackageComparisonTable";
import GlobalHeader from "@/components/GlobalHeader";
import AppFooter from "@/components/AppFooter";

import { API, BACKEND_URL, getAuthHeaders } from "@/lib/api";

export default function Pricing({ user }) {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPackage, setProcessingPackage] = useState(null);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      if (Array.isArray(response.data)) {
        setPackages(response.data);
      } else if (response.data?.packages && Array.isArray(response.data.packages)) {
        setPackages(response.data.packages);
      } else {
        console.error("Unexpected packages data format:", response.data);
        setPackages([]);
      }
    } catch (error) {
      console.error("Failed to fetch packages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPackage = async (packageId) => {
    if (!user) {
      toast.error("Bitte melde dich zuerst an");
      navigate("/");
      return;
    }

    setProcessingPackage(packageId);

    try {
      // For FREE package, directly select it
      if (packageId === "free") {
        const response = await axios.post(
          `${API}/user/select-package`,
          { package_id: packageId },
          { headers: getAuthHeaders() }
        );

        toast.success("FREE-Paket aktiviert!");
        if (window.checkAuth) await window.checkAuth();
        navigate("/dashboard");
        return;
      }

      // For paid packages, go through Stripe
      const response = await axios.post(
        `${API}/stripe/create-checkout`,
        { package_id: packageId },
        { headers: getAuthHeaders() }
      );

      // Redirect to Stripe Checkout
      if (response.data && response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error("Stripe URL not found in response");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Starten des Checkouts");
    } finally {
      setProcessingPackage(null);
    }
  };

  const getPackageIcon = (packageId) => {
    if (packageId.includes("basic")) return Sparkles;
    if (packageId.includes("premium")) return Crown;
    return Zap;
  };

  const getPackageColor = (index) => {
    const colors = [
      "from-sage-100 to-sage-50",
      "from-gold/20 to-gold/5",
      "from-primary/10 to-primary/5"
    ];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const handleLogin = () => {
    navigate("/login?redirect=/dashboard");
  };

  return (
    <div className="min-h-screen bg-background pb-40">
      {/* Global Header */}
      <GlobalHeader user={user} onLogin={handleLogin} showDashboard={true} />

      <div className="pt-20 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="font-serif text-3xl sm:text-4xl text-foreground mb-4">
              Wähle dein Paket
            </h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              Erstelle unvergessliche Events mit unseren flexiblen Paketen.
            </p>
          </motion.div>

          {/* Pricing Cards - New Compact Design */}
          <div className={`grid grid-cols-1 ${packages.length === 4 ? 'md:grid-cols-4' : packages.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6 max-w-6xl mx-auto items-stretch`}>
            {packages.map((pkg, index) => {
              const isPopular = pkg.is_recommended === true;

              return (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="h-full"
                >
                  <CompactPackageCard
                    pkg={pkg}
                    isRecommended={isPopular}
                    onSelect={() => {
                      if (processingPackage !== pkg.id) {
                        handleSelectPackage(pkg.id);
                      }
                    }}
                    buttonText={
                      processingPackage === pkg.id
                        ? "Wird geladen..."
                        : "Jetzt starten"
                    }
                  />
                </motion.div>
              );
            })}
          </div>

          {/* Feature Comparison Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-16"
          >
            <PackageComparisonTable packages={packages} />
          </motion.div>

          {/* Back Button */}
          <div className="text-center mt-12">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              Zurück
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
